import React, { useCallback, useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Drawer } from "antd";

const SCREEN_MARGIN = 100;

export const DrawerFooter = ({ onSave, onClose, saving, saveDisabled = false, saveText = "Сохранить", align = "right" }) => (
  <div className={`nutrition-drawer-footer${align === "left" ? " nutrition-drawer-footer--left" : ""}`}>
    {onSave ? (
      <Button type="primary" loading={saving} disabled={saveDisabled} onClick={onSave}>{saveText}</Button>
    ) : null}
    <Button onClick={onClose} disabled={saving}>Отмена</Button>
  </div>
);

const maxAllowedWidth = (min, max) =>
  typeof window === "undefined"
    ? max
    : Math.min(max, Math.max(min, window.innerWidth - SCREEN_MARGIN));

const clampWidth = (value, min, max, fallback) => {
  const limit = maxAllowedWidth(min, max);
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(fallback, limit);
  return Math.min(limit, Math.max(min, Math.round(n)));
};

const loadWidth = (storageKey, min, max, fallback) => {
  try {
    return clampWidth(localStorage.getItem(storageKey) ?? fallback, min, max, fallback);
  } catch {
    return fallback;
  }
};

const saveWidth = (storageKey, value) => {
  try {
    localStorage.setItem(storageKey, String(value));
  } catch {
    /* ignore */
  }
};

const useResizableWidth = (resizable, width, open) => {
  const { storageKey, min = 360, max = 1200 } = resizable || {};
  const [size, setSize] = useState(() =>
    storageKey ? loadWidth(storageKey, min, max, width) : width
  );

  useEffect(() => {
    if (!open || !resizable) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev, min, max, width));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open, resizable, min, max, width]);

  const onResize = useCallback(
    (next) => setSize(clampWidth(next, min, max, width)),
    [min, max, width]
  );

  const onResizeEnd = useCallback(
    (finalSize) => {
      setSize((prev) => {
        const next = clampWidth(typeof finalSize === "number" ? finalSize : prev, min, max, width);
        if (storageKey) saveWidth(storageKey, next);
        return next;
      });
    },
    [min, max, width, storageKey]
  );

  if (!resizable) return {};
  return {
    size,
    maxSize: maxAllowedWidth(min, max),
    resizable: { onResize, onResizeEnd }
  };
};

const NutritionDrawer = ({ title, open, onClose, width = 420, resizable, children }) => {
  const resizeProps = useResizableWidth(resizable, width, open);
  return (
    <Drawer
      placement="right"
      title={title}
      width={width}
      {...resizeProps}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={false}
      extra={<Button type="text" icon={<CloseOutlined />} aria-label="Закрыть" title="Закрыть" onClick={onClose} />}
      styles={{ body: { display: "flex", flexDirection: "column", paddingTop: 12 } }}
    >
      {open ? children : null}
    </Drawer>
  );
};

export default NutritionDrawer;
